/**
 * Federal return types and their due dates. A due date that falls on a Saturday, Sunday or legal
 * holiday (including Emancipation Day in the District of Columbia) moves to the next business day
 * (IRC §7503). Fiscal-year entities file by the 15th day of the given month after the year ends.
 */

export const RETURN_TYPES = ["1040", "1065", "1120S", "1120", "1041", "990"] as const;
export type ReturnType = (typeof RETURN_TYPES)[number];

export const RETURN_TYPE_LABELS: Record<ReturnType, string> = {
  "1040": "Individual (1040)",
  "1065": "Partnership (1065)",
  "1120S": "S corporation (1120-S)",
  "1120": "C corporation (1120)",
  "1041": "Trust or estate (1041)",
  "990": "Nonprofit (990)",
};

/** The extension form for each return type. */
export const EXTENSION_FORM: Record<ReturnType, string> = { "1040": "4868", "1065": "7004", "1120S": "7004", "1120": "7004", "1041": "7004", "990": "8868" };

const iso = (y: number, m: number, d: number) => `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
const weekday = (y: number, m: number, d: number) => new Date(Date.UTC(y, m - 1, d)).getUTCDay();
/** The n-th given weekday (0 = Sunday) of a month; n = -1 for the last. */
function nthWeekday(y: number, m: number, dow: number, n: number): number {
  if (n > 0) {
    const first = weekday(y, m, 1);
    return 1 + ((dow - first + 7) % 7) + (n - 1) * 7;
  }
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return last - ((weekday(y, m, last) - dow + 7) % 7);
}
/**
 * A fixed-date holiday falling on a weekend is observed on the Friday before or the Monday after.
 * With `banking`, a Saturday holiday isn't moved to the Friday (used for New Year's Day on pay dates).
 */
function observed(y: number, m: number, d: number, banking = false): string {
  const w = weekday(y, m, d);
  const t = new Date(Date.UTC(y, m - 1, d + (w === 6 ? (banking ? 0 : -1) : w === 0 ? 1 : 0)));
  return t.toISOString().slice(0, 10);
}

/**
 * Federal legal holidays in a year, as observed, plus DC Emancipation Day (April 16), which moves
 * IRS deadlines. Banks and payroll ignore Emancipation Day: pass `irs: false` for business days.
 */
export function federalHolidays(y: number, opts: { irs?: boolean; banking?: boolean } = {}): Set<string> {
  // Pay dates (banking: true): a Saturday New Year's Day isn't observed on the Friday before (decision
  // recorded for the October Low; other Saturday holidays keep the Friday).
  const b = opts.banking === true;
  return new Set([
    observed(y, 1, 1, b),
    iso(y, 1, nthWeekday(y, 1, 1, 3)), // Martin Luther King Jr. Day
    iso(y, 2, nthWeekday(y, 2, 1, 3)), // Washington's Birthday
    ...(opts.irs === false ? [] : [observed(y, 4, 16)]), // Emancipation Day (District of Columbia)
    iso(y, 5, nthWeekday(y, 5, 1, -1)), // Memorial Day
    observed(y, 6, 19),
    observed(y, 7, 4),
    iso(y, 9, nthWeekday(y, 9, 1, 1)), // Labor Day
    iso(y, 10, nthWeekday(y, 10, 1, 2)), // Columbus Day
    observed(y, 11, 11),
    iso(y, 11, nthWeekday(y, 11, 4, 4)), // Thanksgiving
    observed(y, 12, 25),
    observed(y + 1, 1, 1, b), // New Year's Day on a Saturday is observed on December 31 (not by banks)
  ]);
}

/**
 * The date, or the next day that is not a weekend or legal holiday. Maryland and Virginia deadlines
 * don't move for DC's Emancipation Day: pass `irs: false` for them. `extraHolidays` adds a state's
 * own holidays (ISO dates) that the firm entered in Firm Settings › Rules and rates.
 */
export function nextBusinessDay(date: string, opts: { irs?: boolean; extraHolidays?: Iterable<string> | null } = {}): string {
  const extra = new Set(opts.extraHolidays ?? []);
  let d = new Date(`${date}T12:00:00Z`);
  for (;;) {
    const s = d.toISOString().slice(0, 10);
    const w = d.getUTCDay();
    if (w !== 0 && w !== 6 && !extra.has(s) && !federalHolidays(d.getUTCFullYear(), opts).has(s)) return s;
    d = new Date(d.getTime() + 86_400_000);
  }
}

/** The 15th (or another day) of the n-th month after a tax year ends in `endMonth` of `endYear`. */
function monthsAfter(endYear: number, endMonth: number, n: number, day = 15): string {
  const idx = endYear * 12 + (endMonth - 1) + n;
  const y = Math.floor(idx / 12);
  const m = (idx % 12) + 1;
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return iso(y, m, Math.min(day, last));
}

/**
 * Original and extended federal due dates for a return. `taxYear` is the year the tax year begins
 * (a fiscal year beginning July 2025 is tax year 2025); `yearEndMonth` is its last month (12 for a
 * calendar year; individuals always use the calendar year).
 */
export function returnDueDates(type: ReturnType, taxYear: number, yearEndMonth = 12): { original: string; extended: string } {
  const endMonth = type === "1040" ? 12 : yearEndMonth;
  const endYear = endMonth === 12 ? taxYear : taxYear + 1;
  let original: string;
  let extended: string;
  switch (type) {
    case "1040":
      original = monthsAfter(endYear, 12, 4);
      extended = monthsAfter(endYear, 12, 10);
      break;
    case "1065":
    case "1120S":
      // 15th day of the 3rd month; extended 6 months.
      original = monthsAfter(endYear, endMonth, 3);
      extended = monthsAfter(endYear, endMonth, 9);
      break;
    case "1120":
      // 15th day of the 4th month, extended 6 months. A June 30 year that began before 2026 keeps
      // the old rule: the 3rd month, extended 7 months.
      if (endMonth === 6 && taxYear < 2026) {
        original = monthsAfter(endYear, endMonth, 3);
        extended = monthsAfter(endYear, endMonth, 10);
      } else {
        original = monthsAfter(endYear, endMonth, 4);
        extended = monthsAfter(endYear, endMonth, 10);
      }
      break;
    case "1041":
      // 15th day of the 4th month; extended 5½ months (September 30 for a calendar year).
      original = monthsAfter(endYear, endMonth, 4);
      extended = monthsAfter(endYear, endMonth, 9, 30);
      break;
    case "990":
      // 15th day of the 5th month; extended 6 months.
      original = monthsAfter(endYear, endMonth, 5);
      extended = monthsAfter(endYear, endMonth, 11);
      break;
  }
  return { original: nextBusinessDay(original), extended: nextBusinessDay(extended) };
}
